import { useEffect, useState } from 'react';
import { confirm } from '../utils/confirm';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { listOvertime, overtimeStaff, createOvertime, updateOvertime, removeOvertime } from '../api/overtime';
import { saveFile } from '../download';
import { fmtDate, fmtVND } from '../utils/format';

function thisMonth() {
  return new Date().toISOString().slice(0, 7);
}
function fmtHM(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h${m ? ` ${m}p` : ''}`;
}
function monthLabel(month) {
  const [y, m] = month.split('-');
  return `Tháng ${m}/${y}`;
}

const WEEKDAYS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
const AVATAR_COLORS = [
  { bg: '#EDE9FE', fg: '#7C3AED' },
  { bg: '#DCFCE7', fg: '#15803D' },
  { bg: '#DBEAFE', fg: '#1D4ED8' },
  { bg: '#FEF3C7', fg: '#B45309' },
  { bg: '#FCE7F3', fg: '#BE185D' },
  { bg: '#E0F2FE', fg: '#0369A1' },
];
function initialsOf(name) {
  const parts = (name || '?').trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase().slice(0, 2) || '?';
}
function colorOf(key) {
  let h = 0;
  for (const c of String(key || '')) h = (h * 31 + c.charCodeAt(0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[h];
}

// Xuất CSV (mở được bằng Excel) — không cần thư viện ngoài.
function exportCsv(month, rows) {
  const header = ['Ngày', 'Nhân viên', 'Bắt đầu', 'Kết thúc', 'Tổng giờ', 'Lương', 'Ghi chú'];
  const lines = rows.map((r) => [
    fmtDate(r.date), r.userName, r.start, r.end, fmtHM(r.minutes), r.pay, r.note || '',
  ]);
  const csv = [header, ...lines].map((row) => row.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  saveFile({ blob, filename: `phan-cong-ca-truc-${month}.csv` });
}

// Lịch tháng: mỗi ô là 1 ngày, hiển thị CHI TIẾT từng ca ngay trong ô (giờ + tên NV + nút xóa).
function CalendarView({ month, rows, canManage, onDelete, onPickDay }) {
  const [y, m] = month.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const firstWeekday = new Date(y, m - 1, 1).getDay(); // 0 = CN

  const byDay = {};
  rows.forEach((r) => {
    const d = new Date(r.date);
    const key = d.getDate();
    (byDay[key] = byDay[key] || []).push(r);
  });

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const today = new Date();
  const isToday = (d) => d && today.getFullYear() === y && today.getMonth() + 1 === m && today.getDate() === d;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        {WEEKDAYS.map((w, i) => (
          <div
            key={w}
            className="small muted"
            style={{ textAlign: 'center', fontWeight: 700, padding: '8px 0', borderRight: i < 6 ? '1px solid var(--border)' : 'none' }}
          >
            {w}
          </div>
        ))}
        {cells.map((d, i) => {
          const today_ = isToday(d);
          return (
            <div
              key={i}
              style={{
                minHeight: 96,
                padding: 7,
                borderTop: '1px solid var(--border)',
                borderRight: (i + 1) % 7 !== 0 ? '1px solid var(--border)' : 'none',
                background: today_ ? 'var(--panel2)' : 'transparent',
                boxShadow: today_ ? 'inset 0 0 0 2px var(--accent)' : 'none',
                opacity: d ? 1 : 0.35,
              }}
            >
              {d && (
                <>
                  <div
                    className="small"
                    style={{
                      fontWeight: 700,
                      marginBottom: 5,
                      color: today_ ? 'var(--accent)' : 'var(--text)',
                      cursor: canManage ? 'pointer' : 'default',
                    }}
                    onClick={() => canManage && onPickDay(d)}
                    title={canManage ? 'Bấm để chọn ngày này cho ô thêm ca' : ''}
                  >
                    {d}
                  </div>
                  {(byDay[d] || []).map((r) => (
                    <div
                      key={r._id}
                      style={{
                        fontSize: 11,
                        background: 'color-mix(in srgb, var(--accent2) 14%, transparent)',
                        borderRadius: 6,
                        padding: '3px 5px',
                        marginBottom: 4,
                        lineHeight: 1.3,
                        position: 'relative',
                      }}
                      title={`${r.userName} · ${r.start}-${r.end} · ${fmtHM(r.minutes)} · ${fmtVND(r.pay)}${r.note ? ' · ' + r.note : ''}`}
                    >
                      <div style={{ fontWeight: 700 }}>
                        {r.start}–{r.end}
                        {canManage && (
                          <span
                            style={{ float: 'right', color: 'var(--danger)', cursor: 'pointer', fontWeight: 700 }}
                            onClick={() => onDelete(r._id)}
                          >
                            ✕
                          </span>
                        )}
                      </div>
                      <div style={{ color: 'var(--text)' }}>{r.userName}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function OvertimePage({ dept = 'Phòng DVKH' }) {
  const { me } = useAuth();
  const deptShort = dept === 'Phòng kế toán' ? 'Kế toán' : 'DVKH';
  // Phân công (tạo ca): MỌI nhân viên của phòng đó (+ GĐ/CV).
  const isGD = me.role === 'GD' || me.role === 'CV' || (me.departments || []).includes(dept);
  // Sửa/xóa ca: chỉ TRƯỞNG PHÒNG của phòng đó (+ GĐ/CV).
  const canManage = me.role === 'GD' || me.role === 'CV' || (me.deptHeadOf || []).includes(dept);
  const toast = useToast();

  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  const [userId, setUserId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [start, setStart] = useState('18:00');
  const [end, setEnd] = useState('21:00');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('calendar'); // 'calendar' | 'list'
  const [editingId, setEditingId] = useState(null);

  function startEdit(r) {
    setEditingId(r._id);
    setUserId(r.userId || r.user?._id || '');
    setDate(new Date(r.date).toISOString().slice(0, 10));
    setStart(r.start);
    setEnd(r.end);
    setNote(r.note || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function cancelEdit() {
    setEditingId(null);
    setNote('');
  }
  function pickDay(d) {
    const [y, m] = month.split('-');
    setDate(`${y}-${m}-${String(d).padStart(2, '0')}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function reload() {
    setLoading(true);
    return listOvertime(month, dept).then(setData).finally(() => setLoading(false));
  }

  useEffect(() => { reload(); }, [month, dept]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isGD || canManage) overtimeStaff(dept).then(setStaff).catch(() => setStaff([]));
  }, [isGD, canManage, dept]);

  function previewMinutes() {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let s = sh * 60 + sm;
    let e = eh * 60 + em;
    if ([s, e].some(Number.isNaN)) return 0;
    if (e <= s) e += 24 * 60;
    return e - s;
  }
  const rate = data?.ratePerHour || 30000;
  const prevMin = previewMinutes();

  function range(s, e) {
    const [sh, sm] = s.split(':').map(Number);
    const [eh, em] = e.split(':').map(Number);
    let a = sh * 60 + sm;
    let b = eh * 60 + em;
    if (b <= a) b += 24 * 60;
    return [a, b];
  }
  function overlaps() {
    const [na, nb] = range(start, end);
    return (data?.rows || []).filter((r) => {
      if (new Date(r.date).toISOString().slice(0, 10) !== date) return false;
      const [ra, rb] = range(r.start, r.end);
      return na < rb && ra < nb;
    });
  }

  async function add() {
    if (busy) return;
    if (!userId) return toast('Chọn nhân viên trực', 'warn');
    if (prevMin <= 0) return toast('Giờ kết thúc phải khác giờ bắt đầu', 'warn');

    const conflicts = overlaps();
    if (conflicts.length) {
      const msg =
        '⚠️ Ca này TRÙNG GIỜ với:\n' +
        conflicts.map((c) => `• ${c.userName}: ${c.start}–${c.end}`).join('\n') +
        '\n\nVẫn thêm ca này?';
      if (!await confirm(msg)) return;
    }

    setBusy(true);
    try {
      if (editingId) {
        await updateOvertime(editingId, { userId, date, start, end, note });
        toast('Đã cập nhật ca trực');
        setEditingId(null);
      } else {
        await createOvertime({ userId, date, start, end, note, dept });
        toast('Đã thêm ca trực');
      }
      setNote('');
      await reload();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function del(id) {
    if (!await confirm('Xóa ca trực này?')) return;
    try {
      await removeOvertime(id);
      toast('Đã xóa ca trực', 'warn');
      await reload();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Lịch trực {deptShort}</h2>
          <div className="sub">Quản lý lịch trực, tổng giờ và lương làm thêm ({fmtVND(data?.ratePerHour || (dept === 'Phòng kế toán' ? 10000 : 30000))}/giờ)</div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            onClick={(e) => e.currentTarget.showPicker?.()}
            style={{ width: 'auto' }}
            title={monthLabel(month)}
          />
          <button className="btn ghost sm" onClick={() => exportCsv(month, data?.rows || [])} disabled={!data?.rows?.length}>
            ⬇ Xuất Excel
          </button>
        </div>
      </div>

      <div className={isGD || canManage ? 'rev-cols' : ''} style={isGD || canManage ? { alignItems: 'stretch' } : undefined}>
        {/* Thêm/sửa ca trực nhanh: NV DVKH tạo; TP DVKH/GĐ sửa. */}
        {(isGD || canManage) && (
          <div className="card" style={{ marginBottom: 0 }}>
            <h3>{editingId ? '✏️ Sửa ca trực' : '➕ Thêm ca trực nhanh'}</h3>
            <div className="two-col" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              <div className="field" style={{ margin: 0 }}>
                <label>Nhân viên trực</label>
                <select value={userId} onChange={(e) => setUserId(e.target.value)}>
                  <option value="">Chọn nhân viên</option>
                  {staff.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {s.employeeCode ? ` (${s.employeeCode})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Ngày</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Giờ bắt đầu</label>
                <input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Giờ kết thúc</label>
                <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </div>
            <div className="row" style={{ gap: 10, alignItems: 'flex-end', marginTop: 10 }}>
              <div className="field" style={{ margin: 0, flex: 1 }}>
                <label>Ghi chú</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nhập ghi chú (nếu có)" />
              </div>
              <button className="btn" onClick={add} disabled={busy} style={{ whiteSpace: 'nowrap' }}>
                {busy ? 'Đang lưu…' : editingId ? '💾 Lưu' : '+ Thêm ca trực'}
              </button>
              {editingId && (
                <button className="btn ghost" onClick={cancelEdit} disabled={busy}>Hủy sửa</button>
              )}
            </div>

            {prevMin > 0 && (
              <div className="small muted" style={{ marginTop: 10 }}>
                Tạm tính: <b style={{ color: 'var(--accent2)' }}>{fmtHM(prevMin)} · {fmtVND(Math.round((prevMin / 60) * rate))}</b>
                {end <= start && ' · (ca qua đêm)'}
              </div>
            )}
            {prevMin > 0 &&
              overlaps().length > 0 &&
              (() => {
                const cf = overlaps();
                return (
                  <div className="small" style={{ marginTop: 6, color: 'var(--danger)', fontWeight: 600 }}>
                    ⚠️ Trùng giờ với: {cf.map((c) => `${c.userName} (${c.start}–${c.end})`).join(', ')}
                  </div>
                );
              })()}
            {staff.length === 0 && (
              <div className="small muted" style={{ marginTop: 8 }}>
                Chưa có nhân viên nào thuộc {dept}. Vào Quản lý nhân viên → gán phòng ban "{dept}" cho nhân viên.
              </div>
            )}
          </div>
        )}

        {/* Thống kê tính tiền theo nhân viên */}
        <div className="card" style={{ marginBottom: 0 }}>
          <h3 style={{ marginBottom: 6 }}>💰 Thống kê tính tiền</h3>
          {data && (
            <div className="small muted" style={{ marginBottom: 10 }}>
              Tổng: <b style={{ color: 'var(--accent2)' }}>{data.totalHours}h · {fmtVND(data.totalPay)}</b>
            </div>
          )}
          {loading ? (
            <div className="empty">Đang tải…</div>
          ) : data && data.summary.length ? (
            data.summary.map((u, i) => {
              const c = colorOf(u.userId || u.userName);
              return (
                <div
                  key={u.userId}
                  className="row"
                  style={{ gap: 8, alignItems: 'center', padding: '7px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}
                >
                  <span
                    style={{
                      width: 28, height: 28, borderRadius: '50%', background: c.bg, color: c.fg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0,
                    }}
                  >
                    {initialsOf(u.userName)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="small" style={{ fontWeight: 600 }}>{u.userName}</div>
                    <div className="small muted">{u.hours}h · {u.shifts} ca</div>
                  </div>
                  <b style={{ color: 'var(--accent2)', whiteSpace: 'nowrap' }}>{fmtVND(u.pay)}</b>
                </div>
              );
            })
          ) : (
            <div className="empty">Chưa có ca trực nào trong tháng này</div>
          )}
        </div>
      </div>

      {/* Lịch ca trực — xem dạng lịch (chi tiết từng ca ngay trong ô) hoặc bảng */}
      <div className="card">
        <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>🗓️ Lịch ca trực {monthLabel(month)}</h3>
          <div className="seg">
            <button className={view === 'calendar' ? 'active' : ''} onClick={() => setView('calendar')}>Lịch</button>
            <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>Bảng</button>
          </div>
        </div>

        {loading ? (
          <div className="empty">Đang tải…</div>
        ) : view === 'calendar' ? (
          <CalendarView month={month} rows={data ? data.rows : []} canManage={canManage} onDelete={del} onPickDay={pickDay} />
        ) : (
          <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Ngày</th>
                <th>Nhân viên</th>
                <th className="c">Bắt đầu</th>
                <th className="c">Kết thúc</th>
                <th className="r">Thời gian</th>
                <th className="r">Lương</th>
                <th>Ghi chú</th>
                {canManage && <th className="c">Thao tác</th>}
              </tr>
            </thead>
            <tbody>
              {data && data.rows.length ? (
                data.rows.map((r) => (
                  <tr key={r._id}>
                    <td>{fmtDate(r.date)}</td>
                    <td>{r.userName}</td>
                    <td className="c">{r.start}</td>
                    <td className="c">{r.end}</td>
                    <td className="r">{fmtHM(r.minutes)}</td>
                    <td className="r">{fmtVND(r.pay)}</td>
                    <td className="small muted">{r.note || '—'}</td>
                    {canManage && (
                      <td className="c">
                        <div className="row" style={{ gap: 6, justifyContent: 'center' }}>
                          <button className="btn sm ghost" onClick={() => startEdit(r)}>Sửa</button>
                          <button className="btn sm danger" onClick={() => del(r._id)}>Xóa</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={canManage ? 8 : 7} className="empty">Chưa có ca trực nào</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </>
  );
}
